export function createFukushizaiDetailSyncService(deps) {
    const {
        entrydataRef,
        entrydetailRef,
        selectedFukushizaiContextRef,
        fukushizaiDetailSyncingRef,
        fukushizaiRepresentativeRowRef,
        FUKUSHIZAI_BASE_PANEL_TYPE,
        pendingFukushizaiCopy,
        selectedSetItem,
        selectedGroup,
        isEmpty,
        isEnabled,
        DicVal,
        normalizeGroupNo,
        readRowValueIgnoreCase,
        normalizeFukushizaiContext,
        readFukushizaiContextFromRow,
        normalizeFukushizaiDetailMatchInfo,
        findFukushizaiDetailRowIndexByMatchInfo,
        findFukushizaiDetailExactRowIndexByMatchInfo,
        getDetailRowsByPanelType,
        pickFukushizaiRepresentativeRow,
        resolveFukushizaiLazyDetailColorNow,
        createFukushizaiGroupDetailRow,
        isFukushizaiVerticalMasterSourceColumnId,
        clearFukushizaiMasterNameFields,
        extractMasterCodeFromDisplayValue,
        clearFukushizaiDisplayProjectionFields,
        updateSelectedFukushizaiContext,
        clearFukushizaiMergedFields,
        mergeFukushizaiRepresentativeRowToEntry,
        rerender,
        setSelectedSetItem,
        setSelectedGroup,
        debugFukushizaiSyncLog,
        refreshFukushizaiSectionViews
    } = deps;

    // 副資材明細の対象行へ更新値を反映します。
    function applyFukushizaiDetailRowUpdates(matchInfo, updates, options = {}) {
        const detailRows = getDetailRowsByPanelType(entrydetailRef.current, FUKUSHIZAI_BASE_PANEL_TYPE);
        if (!Array.isArray(detailRows) || detailRows.length === 0) {
            return { changed: false, index: -1, row: null, context: normalizeFukushizaiContext(matchInfo) };
        }

        const match = normalizeFukushizaiDetailMatchInfo(matchInfo);
        const exactRowOnly = isEnabled(DicVal(options, "exactRowOnly", false));
        let index = exactRowOnly
            ? findFukushizaiDetailExactRowIndexByMatchInfo(detailRows, match)
            : findFukushizaiDetailRowIndexByMatchInfo(detailRows, match);

        if (index === -1) {
            // 代表行へのフォールバックを許可している場合だけ候補行へ寄せます。
            if (isEnabled(DicVal(options, "allowRepresentativeFallback", false))) {
                const picked = pickFukushizaiRepresentativeRow(detailRows, selectedFukushizaiContextRef.current, false);
                if (!isEmpty(picked) && picked.index >= 0) {
                    index = picked.index;
                }
            } else if (isEnabled(DicVal(options, "allowCreateIfMissing", false))) {
                // item/group/color が揃う場合だけ不足行を新規作成します。
                const resolvedColor = match.colorCd !== ""
                    ? match.colorCd
                    : resolveFukushizaiLazyDetailColorNow(match);
                if (match.itemCd !== "" && match.groupNo !== "" && resolvedColor !== "") {
                    const createdRow = createFukushizaiGroupDetailRow(match.itemCd, match.groupNo, resolvedColor, null, false);
                    if (!isEmpty(createdRow)) {
                        detailRows.push(createdRow);
                        index = detailRows.length - 1;
                    }
                }
            } else {
                debugFukushizaiSyncLog("[Kakousiji][fukushizai detail sync skipped: no exact row]", {
                    match,
                    updates,
                    syncSource: DicVal(options, "syncSource", "")
                });
            }
        }

        if (index === -1 || isEmpty(detailRows[index])) {
            return { changed: false, index: -1, row: null, context: normalizeFukushizaiContext(matchInfo) };
        }

        const baseRow = detailRows[index];
        const targetIndexes = [index];
        if (isEnabled(DicVal(options, "sameItemGroup", false))) {
            const itemCd = String(readRowValueIgnoreCase(baseRow, "SSDZ_ITMCITEMCD") ?? "").trim();
            const groupNo = normalizeGroupNo(readRowValueIgnoreCase(baseRow, "SSDZ_FUKUSIZAICOLORGRP"));
            detailRows.forEach((row, rowIndex) => {
                if (rowIndex === index || isEmpty(row)) return;
                const rowItem = String(readRowValueIgnoreCase(row, "SSDZ_ITMCITEMCD") ?? "").trim();
                const rowGroup = normalizeGroupNo(readRowValueIgnoreCase(row, "SSDZ_FUKUSIZAICOLORGRP"));
                if (itemCd !== "" && rowItem !== itemCd) return;
                if (groupNo !== "" && rowGroup !== groupNo) return;
                targetIndexes.push(rowIndex);
            });
        }

        let changed = false;
        let firstChangedIndex = -1;
        let firstChangedRow = null;
        targetIndexes.forEach((targetIndex) => {
            const currentRow = detailRows[targetIndex];
            if (isEmpty(currentRow)) return;

            const nextRow = { ...currentRow };
            let rowChanged = false;
            Object.keys(updates || {}).forEach((key) => {
                if (isEmpty(key)) return;
                if (nextRow[key] === updates[key]) return;

                // 縦持ち master 系は code 変更時に name 系キャッシュを消します。
                if (isFukushizaiVerticalMasterSourceColumnId(key)) {
                    clearFukushizaiMasterNameFields(nextRow, key, {
                        timing: String(DicVal(options, "timing", DicVal(options, "syncSource", "detail-row-update")) ?? "detail-row-update"),
                        panelType: "",
                        oldCode: extractMasterCodeFromDisplayValue(nextRow[key]),
                        newCode: extractMasterCodeFromDisplayValue(updates[key]),
                        reason: "code-changed"
                    });
                }

                nextRow[key] = updates[key];
                rowChanged = true;
            });

            if (!rowChanged) return;

            clearFukushizaiDisplayProjectionFields(nextRow);
            detailRows[targetIndex] = nextRow;
            changed = true;
            if (firstChangedIndex === -1) {
                firstChangedIndex = targetIndex;
                firstChangedRow = nextRow;
            }
        });

        if (!changed) {
            return {
                changed: false,
                index: index,
                row: baseRow,
                context: readFukushizaiContextFromRow(baseRow),
                updatedCount: 0
            };
        }

        const context = readFukushizaiContextFromRow(firstChangedRow);
        fukushizaiRepresentativeRowRef.current = {
            rowid: String(DicVal(firstChangedRow, "rowid", firstChangedIndex) ?? firstChangedIndex),
            index: firstChangedIndex
        };
        return {
            changed: true,
            index: firstChangedIndex,
            row: firstChangedRow,
            context: context,
            updatedCount: targetIndexes.length
        };
    }

    // 明細更新後の代表行反映と再描画をまとめて行います。
    function finishFukushizaiDetailSync(result, previewOverride = pendingFukushizaiCopy, options = {}) {
        if (isEmpty(result) || !isEnabled(DicVal(result, "changed", false))) return false;

        const context = normalizeFukushizaiContext(DicVal(result, "context", null));
        const refresh = DicVal(options, "refresh", true) !== false;
        const flushBeforeRefresh = isEnabled(DicVal(options, "flushBeforeRefresh", false));
        const shouldRerender = DicVal(options, "rerender", true) !== false;
        const updateSelection = DicVal(options, "updateSelection", true) !== false;
        const previousSyncing = fukushizaiDetailSyncingRef.current;
        fukushizaiDetailSyncingRef.current = true;

        debugFukushizaiSyncLog("[Kakousiji][fukushizai detail sync]", {
            context,
            rowid: String(DicVal(DicVal(result, "row", null), "rowid", "") ?? ""),
            index: DicVal(result, "index", -1),
            refresh,
            flushBeforeRefresh,
            shouldRerender,
            updateSelection
        });

        try {
            if (updateSelection) {
                updateSelectedFukushizaiContext(context);
                if (selectedSetItem !== context.itemCd) setSelectedSetItem(context.itemCd);
                if (selectedGroup !== context.groupNo) setSelectedGroup(context.groupNo);
            }

            clearFukushizaiMergedFields(entrydataRef.current);
            mergeFukushizaiRepresentativeRowToEntry(entrydataRef.current, entrydetailRef.current, context);

            // 画面反映を止めていない場合だけ section 表示を再構築します。
            if (refresh) {
                refreshFukushizaiSectionViews(context.itemCd, context.groupNo, previewOverride, context);
            }
            if (shouldRerender) rerender();
            return true;
        } finally {
            fukushizaiDetailSyncingRef.current = previousSyncing;
        }
    }

    // もっとも単純な「1 行更新して同期完了まで行う」入口です。
    function updateFukushizaiDetailRowValue(matchInfo, updates) {
        const result = applyFukushizaiDetailRowUpdates(matchInfo, updates);
        return finishFukushizaiDetailSync(result);
    }

    return {
        applyFukushizaiDetailRowUpdates,
        finishFukushizaiDetailSync,
        updateFukushizaiDetailRowValue
    };
}
